import './brokerCryptoTestDisclaimer.scss'
import React, { useEffect, useRef }  from 'react';
const { formatText, FormattedText } = require('core/i18n').i18n;

// @components
import { OKLink } from 'commonsComponents/OKLink';
import { CryptoButton } from '../brokerCryptoButton/brokerCryptoButton';
import { LoadingSection } from 'commonsComponents/loadingSection';
import { OKSectionBox } from 'commonsComponents/OKSectionBox';
import FormattedTextWithComponents from 'components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents';
import BrokerCryptoFrame from '../brokerCryptoFrame/brokerCryptoFrame';

// @helpers
import { getAllQuestionsFromCategories } from "mifid/utilities/mifidCryptoTestHelper";
import PropTypes from 'prop-types';
import { taggingEventHandled } from 'utilities/taggingEventHandled';
import { recordPage, PROC_BROKER_CONVENIENCE_CRYPTO_TEST } from 'utilities/tagging';

// @constants
import { DATA_PROTECTION } from 'constants/links';
import { MYPROFILE_BROKER_CRYPTO_URL } from 'constants/index';
import { REASON_EXPIRED_TEST, REASON_NO_PREVIOUS_TEST } from 'mifid/constants/constants';

export default function BrokerCryptoTestDisclaimer({
    onRetakeTest,
    immBrokerConvenienceMiFIDTest,
    verifyDocumentSigned,
    history,
    requestCryptoDocumentation
}) {
    const testReason = immBrokerConvenienceMiFIDTest.getIn(['mifidv2','testToEvaluate', 'reason']);
    const isExpiredTest = testReason === REASON_EXPIRED_TEST;
    const isFirstTimeTest = testReason === REASON_NO_PREVIOUS_TEST;
    const documentHasSigned = immBrokerConvenienceMiFIDTest.getIn(['test', 'documentationHasSigned']);

    useEffect(() => {
        verifyDocumentSigned();
    }, []);

    const prevMiFIDTestRef = useRef();

    useEffect(() => {
        const prevMiFIDTest = prevMiFIDTestRef.current;

        if (prevMiFIDTest) {
            const currentLoading = immBrokerConvenienceMiFIDTest.getIn(['test', 'verifyDocumentationLoading']);
            const prevLoading = prevMiFIDTest.getIn(['test', 'verifyDocumentationLoading']);

            if (prevLoading && !currentLoading) {
                if (isFirstTimeTest || isExpiredTest || !documentHasSigned) {
                    const customData = {
                        pageName: "/testCrypto",
                        sectionFamily: "wealth",
                        sectionCategory: "services",
                        sectionName: "crypto",
                        interactionType: "page",
                        serviceType: "productInformation"
                    };
                    recordPage(customData);

                    if (!documentHasSigned) {
                        requestCryptoDocumentation()
                    }
                } else {
                    history.push(MYPROFILE_BROKER_CRYPTO_URL);
                }
            }
        }

        prevMiFIDTestRef.current = immBrokerConvenienceMiFIDTest;
    }, [immBrokerConvenienceMiFIDTest]);


    const documentHasSignedLoading = immBrokerConvenienceMiFIDTest.getIn(['test', 'verifyDocumentationLoading']);
    const initDocumentationLoading = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'cryptoOperationProcessIdLoading']);

    if (documentHasSignedLoading || initDocumentationLoading) {
        return(
            <OKSectionBox>
                <LoadingSection />
            </OKSectionBox>
        );
    }

    const title = (
        <h2 className="broker-crypto-disclaimer__title">
            {formatText("brokerCryptoTest-disclaimerTitle")}
        </h2>
    );
    const footerLink = (
        <OKLink
            extraClass="crypto-test-footer__link"
            target="_blank"
            to={DATA_PROTECTION}
        >
            <FormattedText value="brokerCryptoTest-disclaimer_footerLinkText"/>
        </OKLink>
    );
    const footerText = (
        <p className="broker-crypto-disclaimer__footer">
            <h2 className="broker-crypto-disclaimer__footer-hidden-header">
                {formatText('brokerCryptoTest-disclaimer_footerCaption')}
            </h2>
            <FormattedTextWithComponents value="brokerCryptoTest-disclaimer_footer" injectedComponents={[footerLink]} />
        </p>
    );

    const immTest = immBrokerConvenienceMiFIDTest.get("test");
    const immSchema = immTest.get("schema");
    const immCategories = immSchema.get("categories");
    const { answeredQuestions, unansweredQuestions } = getAllQuestionsFromCategories(immCategories);

    const contentText = isExpiredTest ? (
        <FormattedText
            value="brokerCryptoTest-disclaimer_expiredText"
            injectedStrings={[unansweredQuestions.size]}
        />
    ) : (
        <FormattedText
            value="brokerCryptoTest-disclaimer_documentationIncompleteText"
            injectedStrings={[unansweredQuestions.size]}
        />
    )

    const content = (
        <div className="broker-crypto-disclaimer__content-container">
            <div className="broker-crypto-disclaimer__content-image"/>
            <div className="broker-crypto-disclaimer__content-text">
                <p>
                    {contentText}
                </p>
            </div>
        </div>
    );

    const retakeTestFirstTime = () => {
        taggingEventHandled(PROC_BROKER_CONVENIENCE_CRYPTO_TEST, "startTest", {pathname: "/testCrypto"});
        onRetakeTest(false);
    }

    const buttons = (
        <div className="broker-crypto-disclaimer__cta-container">
            <CryptoButton onClick={() => answeredQuestions.isEmpty() ? retakeTestFirstTime() : onRetakeTest(false)}>
                <FormattedText className="broker-crypto-disclaimer__cta" value="brokerCryptoTest-disclaimerCTAFirstTime" />
            </CryptoButton>
        </div>
    );
    return (
        <BrokerCryptoFrame immBrokerConvenienceMiFIDTest={immBrokerConvenienceMiFIDTest} title={formatText("brokerCryptoTest-frameTitle")}>
            <div className="broker-crypto-disclaimer__container">
                {title}
                {content}
                {buttons}
                {footerText}
            </div>
        </BrokerCryptoFrame>
    )
}

BrokerCryptoTestDisclaimer.propTypes = {
    history: PropTypes.object.isRequired,
    immBrokerConvenienceMiFIDTest: PropTypes.object.isRequired,
    onRetakeTest: PropTypes.func.isRequired,
    requestCryptoDocumentation: PropTypes.func.isRequired,
    verifyDocumentSigned: PropTypes.func.isRequired
};